import theme from 'styles/theme';
import { liveChatAvatar } from 'images';
import { LiveChatPreFillParams } from './types';
import {
  SALESFORCE_LIVECHAT_BUTTON_URL,
  SALESFORCE_LIVECHAT_PROFILE_NAME,
  SALESFORCE_LIVECHAT_DEPLOYMENT_ID,
  SALESFORCE_LIVECHAT_BUTTON_ID,
  SALESFORCE_LIVECHAT_AGENT_DEV_NAME,
} from 'config/env';
import { CONTACT_US_URL } from 'config/constants';
import { getLiveChatAnchor } from './domHelpers';
import { captureErrorInSentry } from 'lib/captureInSentry';
import { requestNotificationPermission, pushMessageNotification } from './notifications';

export const getEmbeddedSvc = () => window.embedded_svc;

export const getAreAgentsAvailable = () => {
  const svc = getEmbeddedSvc();
  return !!svc && !svc.isButtonDisabled;
};

export const launchChatWindow = () => {
  if (getAreAgentsAvailable()) {
    getEmbeddedSvc()?.onHelpButtonClick();
  } else {
    window.open(CONTACT_US_URL, '_blank');
  }
};

const getExtraPrechatFormDetails = (params: LiveChatPreFillParams) => {
  const extraPrechatFormDetails = [
    {
      label: 'Status',
      value: 'New',
      displayToAgent: false,
    },
    {
      label: 'Origin',
      value: 'Live Chat',
      displayToAgent: false,
    },
    {
      label: 'RecordTypeId',
      value: '01290000000WxpX',
      displayToAgent: false,
    },
    {
      label: 'WebOrigin__c',
      value: 'Qantas Self Service',
      displayToAgent: false,
    },
    {
      label: 'Web_Origin__c',
      value: 'Qantas Self Service',
      displayToAgent: false,
    },
    {
      label: 'Subject',
      value: `Self Service | ${params.name} | ${params.bookingReference}`,
      displayToAgent: false,
    },
    {
      label: 'Query_Type__c',
      value: 'Self Service',
      displayToAgent: true,
    },

    {
      label: 'First Name',
      displayToAgent: false,
    },
    {
      label: 'Last Name',
      displayToAgent: false,
    },
    {
      label: 'Supplied_Name_Manual__c',
      value: params.name,
      displayToAgent: true,
    },
    {
      label: 'Supplied_Email_Manual__c',
      value: params.emailAddress,
      displayToAgent: true,
    },
    {
      label: 'Supplied_Phone_Manual__c',
      value: params.phoneNumber,
      displayToAgent: true,
    },
  ];

  if (params.bookingReference) {
    extraPrechatFormDetails.push({
      label: 'Booking_Number__c',
      value: params.bookingReference,
      displayToAgent: true,
    });
  }

  if (params.accountId) {
    extraPrechatFormDetails.push({
      label: 'QFF_Number__c',
      value: params.accountId,
      displayToAgent: false,
    });
  }

  return extraPrechatFormDetails;
};

const getExtraPrechatInfo = (params: LiveChatPreFillParams) => {
  const caseEntityFieldMaps = [
    {
      doCreate: true,
      doFind: false,
      fieldName: 'Supplied_Name_Manual__c',
      isExactMatch: true,
      label: 'Supplied_Name_Manual__c',
    },
    {
      doCreate: true,
      doFind: false,
      fieldName: 'Supplied_Email_Manual__c',
      isExactMatch: true,
      label: 'Supplied_Email_Manual__c',
    },
    {
      doCreate: true,
      doFind: false,
      fieldName: 'Supplied_Phone_Manual__c',
      isExactMatch: true,
      label: 'Supplied_Phone_Manual__c',
    },
    {
      doCreate: true,
      doFind: false,
      fieldName: 'Type',
      isExactMatch: true,
      label: 'Type',
    },
    {
      doCreate: true,
      doFind: false,
      fieldName: 'Status',
      isExactMatch: true,
      label: 'Status',
    },
    {
      doCreate: true,
      doFind: false,
      fieldName: 'Origin',
      isExactMatch: true,
      label: 'Origin',
    },
    {
      doCreate: true,
      doFind: false,
      fieldName: 'RecordTypeId',
      isExactMatch: true,
      label: 'RecordTypeId',
    },
    {
      doCreate: true,
      doFind: false,
      fieldName: 'Subject',
      isExactMatch: true,
      label: 'Subject',
    },
    {
      doCreate: true,
      doFind: false,
      fieldName: 'WebOrigin__c',
      isExactMatch: true,
      label: 'WebOrigin__c',
    },
    {
      doCreate: true,
      doFind: false,
      fieldName: 'Web_Origin__c',
      isExactMatch: true,
      label: 'Web_Origin__c',
    },
    {
      doCreate: true,
      doFind: false,
      fieldName: 'Query_Type__c',
      isExactMatch: true,
      label: 'Query_Type__c',
    },
  ];

  if (params.bookingReference) {
    caseEntityFieldMaps.push({
      doCreate: true,
      doFind: false,
      fieldName: 'Booking_Number__c',
      isExactMatch: true,
      label: 'Booking_Number__c',
    });
  }

  if (params.accountId) {
    caseEntityFieldMaps.push({
      doCreate: true,
      doFind: false,
      fieldName: 'QFF_Number__c',
      isExactMatch: true,
      label: 'QFF_Number__c',
    });
  }

  const extraPrechatInfo = [
    {
      entityName: 'Contact',
      linkToEntityName: '',
      saveToTranscript: '',
      entityFieldMaps: [
        {
          doCreate: false,
          doFind: false,
          fieldName: 'FirstName',
          label: 'First Name',
        },
        {
          doCreate: false,
          doFind: false,
          fieldName: 'LastName',
          label: 'Last Name',
        },
      ],
    },
    {
      entityName: 'Case',
      showOnCreate: false,
      saveToTranscript: 'CaseId',
      entityFieldMaps: caseEntityFieldMaps,
    },
  ];

  return extraPrechatInfo;
};

export const initLiveChat = () => {
  const gslbBaseURL = 'https://service.force.com';
  const embeddedSvc = getEmbeddedSvc();

  if (!embeddedSvc) {
    console.log('LiveChat disabled'); // eslint-disable-line no-console
    return;
  }

  embeddedSvc.settings.targetElement = getLiveChatAnchor();
  embeddedSvc.settings.displayHelpButton = true;
  embeddedSvc.settings.autoOpenPostChat = true;
  embeddedSvc.settings.language = 'en';
  embeddedSvc.settings.defaultMinimizedText = 'Live Chat available';
  embeddedSvc.settings.disabledMinimizedText = 'Live Chat unavailable';
  embeddedSvc.settings.storageDomain = window.location.hostname;
  embeddedSvc.settings.enabledFeatures = ['LiveAgent'];
  embeddedSvc.settings.entryFeature = 'LiveAgent';
  embeddedSvc.settings.widgetFontSize = theme.fontSizes.base;
  embeddedSvc.settings.avatarImgURL = liveChatAvatar;

  try {
    embeddedSvc.addEventHandler('onChatRequestSuccess', requestNotificationPermission);
    embeddedSvc.addEventHandler('onAgentMessage', pushMessageNotification);
    embeddedSvc.addEventHandler('onAgentRichMessage', pushMessageNotification);

    embeddedSvc.init(
      'https://qantashotels.my.salesforce.com',
      SALESFORCE_LIVECHAT_BUTTON_URL,
      gslbBaseURL,
      '00D90000000ZL1d',
      SALESFORCE_LIVECHAT_PROFILE_NAME,
      {
        baseLiveAgentContentURL: 'https://c.la1-c2-ukb.salesforceliveagent.com/content',
        deploymentId: SALESFORCE_LIVECHAT_DEPLOYMENT_ID,
        buttonId: SALESFORCE_LIVECHAT_BUTTON_ID,
        baseLiveAgentURL: 'https://d.la1-c2-ukb.salesforceliveagent.com/chat',
        eswLiveAgentDevName: SALESFORCE_LIVECHAT_AGENT_DEV_NAME,
        isOfflineSupportEnabled: false,
      },
    );
  } catch (e) {
    captureErrorInSentry(e);
  }
};

export const preFillLiveChat = (params: LiveChatPreFillParams) => {
  const embeddedSvc = getEmbeddedSvc();

  if (!embeddedSvc) {
    console.log('LiveChat disabled'); // eslint-disable-line no-console
    return;
  }

  embeddedSvc.settings.extraPrechatFormDetails = getExtraPrechatFormDetails(params);
  embeddedSvc.settings.extraPrechatInfo = getExtraPrechatInfo(params);

  initLiveChat();
};
